import { useState, useEffect } from 'react';
import { Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { accessControlAction } from '@/lib/api';

interface SwishPaymentProps {
  deviceId: string;
  amount: number; // öre
  onPaid: (paymentId: string) => void;
  onCancel: () => void;
}

type Status = 'idle' | 'creating' | 'pending' | 'succeeded' | 'failed';

export function SwishPayment({ deviceId, amount, onPaid, onCancel }: SwishPaymentProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [swishUrl, setSwishUrl] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createPayment = async () => {
    setStatus('creating');
    setError(null);

    const result = await accessControlAction<{
      payment_id: string;
      swish_url: string;
      qr_data: string;
      status: string;
    }>('create-access-payment', {
      device_id: deviceId,
      payment_method: 'swish',
    });

    if (result.error || !result.data) {
      setError(result.error || 'Kunde inte skapa Swish-betalning');
      setStatus('failed');
      return;
    }

    setPaymentId(result.data.payment_id);
    setSwishUrl(result.data.swish_url);
    setQrData(result.data.qr_data);
    setStatus('pending');
  };

  // Poll for payment completion
  useEffect(() => {
    if (status !== 'pending' || !paymentId) return;

    const interval = setInterval(async () => {
      const result = await accessControlAction<{
        payment_id: string;
        status: string;
      }>('poll-access-payment', {
        payment_id: paymentId,
      });

      if (result.data?.status === 'succeeded') {
        setStatus('succeeded');
        onPaid(paymentId);
        return;
      }

      if (result.data?.status === 'failed' || result.data?.status === 'cancelled') {
        setError('Betalningen misslyckades eller avbröts');
        setStatus('failed');
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [status, paymentId, onPaid]);

  const amountKr = (amount / 100).toFixed(2);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Swish-betalning</h3>
      <p className="text-sm text-muted-foreground">
        Belopp: <strong>{amountKr} kr</strong>
      </p>

      {status === 'idle' && (
        <Button onClick={createPayment} className="w-full">
          Betala {amountKr} kr med Swish
        </Button>
      )}

      {status === 'creating' && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Skapar Swish-betalning...</span>
        </div>
      )}

      {status === 'pending' && (
        <div className="space-y-4">
          {qrData && (
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-lg">
                <QRCodeSVG value={qrData} size={200} />
              </div>
            </div>
          )}
          <p className="text-sm text-center text-muted-foreground">
            Scanna QR-koden med Swish-appen eller klicka nedan:
          </p>
          {swishUrl && (
            <Button asChild className="w-full">
              <a href={swishUrl}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Öppna Swish
              </a>
            </Button>
          )}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Väntar på betalning...</span>
          </div>
        </div>
      )}

      {status === 'succeeded' && (
        <div className="flex items-center justify-center gap-2 py-4 text-green-600">
          <CheckCircle2 className="h-5 w-5" />
          <span>Betalning genomförd!</span>
        </div>
      )}

      {status === 'failed' && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button onClick={createPayment} variant="outline" className="w-full">
            Försök igen
          </Button>
        </div>
      )}

      {status !== 'succeeded' && (
        <Button variant="ghost" onClick={onCancel} className="w-full">
          Avbryt
        </Button>
      )}
    </div>
  );
}
